import type { ReactNode } from 'react';

import { cn } from '@pesto/utils';
import { type Column, flexRender, type Row, type Table as TanstackTable } from '@tanstack/react-table';
import * as React from 'react';
import { cloneElement } from 'react';

import { ContextMenu, ContextMenuContent, ContextMenuTrigger } from '../ContextMenu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../Table';
import { DataTablePagination } from './DataTablePagination';

function getCommonPinningStyles<TData>({
  column,
  withBorder = false,
}: {
  column: Column<TData>;
  withBorder?: boolean;
}): React.CSSProperties {
  const isPinned = column.getIsPinned();
  const isLastLeftPinnedColumn = isPinned === 'left' && column.getIsLastColumn('left');
  const isFirstRightPinnedColumn = isPinned === 'right' && column.getIsFirstColumn('right');

  return {
    boxShadow: withBorder
      ? isLastLeftPinnedColumn
        ? '-4px 0 4px -4px hsl(var(--border)) inset'
        : isFirstRightPinnedColumn
          ? '4px 0 4px -4px hsl(var(--border)) inset'
          : undefined
      : undefined,
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
    opacity: isPinned ? 0.97 : 1,
    position: isPinned ? 'sticky' : 'relative',
    background: isPinned ? 'hsl(var(--background))' : 'hsl(var(--background))',
    width: column.getSize(),
    zIndex: isPinned ? 1 : 0,
  };
}

interface DataTableProps<TData> extends React.HTMLAttributes<HTMLDivElement> {
  table: TanstackTable<TData>;

  floatingBar?: React.ReactNode | null;
  showPagination?: boolean;
  rowContextMenu?: (row: Row<TData>) => ReactNode;
}

export function DataTable<TData>({
  table,
  floatingBar = null,
  children,
  className,
  showPagination = true,
  rowContextMenu,
  ...props
}: DataTableProps<TData>) {
  return (
    <div className={cn('w-full space-y-2.5 overflow-auto', className)} {...props}>
      {children}
      <div className="overflow-hidden rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={`table-header-row-${headerGroup.id}`}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={`table-header-cell-${header.id}`}
                      className="whitespace-nowrap"
                      colSpan={header.colSpan}
                      style={{
                        ...getCommonPinningStyles({ column: header.column }),
                      }}
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => {
                const rowOriginal = row.original;

                const tableCell = () => {
                  if (React.isValidElement(rowOriginal)) {
                    return <TableCell colSpan={table.getAllColumns().length}>{cloneElement(rowOriginal)}</TableCell>;
                  }

                  return row.getVisibleCells().map((cell) => {
                    return (
                      <TableCell
                        key={`table-cell-${cell.id}`}
                        className="whitespace-nowrap"
                        style={{
                          ...getCommonPinningStyles({ column: cell.column }),
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    );
                  });
                };

                if (rowContextMenu) {
                  return (
                    <ContextMenu key={`table-row-${row.id}`}>
                      <ContextMenuTrigger asChild>
                        <TableRow data-state={row.getIsSelected() && 'selected'}>{tableCell()}</TableRow>
                      </ContextMenuTrigger>
                      <ContextMenuContent>{rowContextMenu(row)}</ContextMenuContent>
                    </ContextMenu>
                  );
                }

                return (
                  <TableRow key={`table-row-${row.id}`} data-state={row.getIsSelected() && 'selected'}>
                    {tableCell()}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={table.getAllColumns().length} className="h-24 text-center">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex flex-col gap-2.5">
        {showPagination && <DataTablePagination table={table} />}
        {table.getFilteredSelectedRowModel().rows.length > 0 && floatingBar}
      </div>
    </div>
  );
}
